import { useEffect, useRef } from "react";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import logger from "../utils/logger";

interface UseJoinGroupProps {
  connection: HubConnection | null;
  sessionId: number;
}

const useJoinGroup = ({ connection, sessionId }: UseJoinGroupProps) => {
  // Use a ref to track which sessions we've already joined
  const joinedSessionRef = useRef<number | null>(null);

  // Handle component mounting/unmounting
  useEffect(() => {
    // Skip if no change in sessionId or connection
    if (!connection || !sessionId || sessionId === 0) return;

    const joinGroup = async () => {
      if (connection.state === HubConnectionState.Connected) {
        // Only join if we haven't joined this session already
        //if (joinedSessionRef.current !== sessionId) {
          try {
            logger.debug(`Attempting to join group ${sessionId}`);
            await connection.invoke("JoinGroup", sessionId);
            logger.debug(`Successfully joined group ${sessionId}`);
            joinedSessionRef.current = sessionId;
          } catch (error) {
            logger.error("Error joining group:", error);
          }
        //} else {
        //  console.log(`Already joined session ${sessionId}, skipping join`);
        //}
      }
    };

    // Set up reconnection handler — use the lifecycle callback, not a hub method
    let active = true;
    const reconnectedHandler = (_connectionId?: string) => {
      if (!active) return;
      logger.debug("Connection re-established. Checking if rejoin needed.");
      joinGroup();
    };

    connection.onreconnected(reconnectedHandler);
    
    // Try to join when effect runs
    joinGroup();

    // Clean up when component unmounts or sessionId changes
    return () => {
      active = false; // prevent stale handler from running after cleanup
      
      // Only leave if we've actually joined this session
      if (connection.state === HubConnectionState.Connected && 
          joinedSessionRef.current === sessionId) {
        logger.debug(`Leaving group ${sessionId} on cleanup`);
        
        connection
          .invoke("LeaveGroup", sessionId)
          .catch((err) =>
            logger.error("Error leaving group on cleanup:", err)
          );
        
        joinedSessionRef.current = null;
      }
    };
  }, [connection, connection?.state, sessionId]);

  // Handle browser tab/window closing - no changes needed here
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (
        connection &&
        connection.state === HubConnectionState.Connected &&
        sessionId &&
        joinedSessionRef.current === sessionId
      ) {
        try {
          logger.debug(`Leaving group ${sessionId} on page unload`);

          // Method 1: Try direct invocation
          connection.invoke("LeaveGroup", sessionId).catch(() => {
            logger.debug("Direct invoke failed, using sendBeacon");
          });

          // Method 2: Use sendBeacon as backup
          const url = `${window.location.origin}/api/cross/session/${sessionId}/leave`;
          navigator.sendBeacon(url);
          
          joinedSessionRef.current = null;
        } catch (e) {
          logger.error("Failed to send leave notification:", e);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [connection, sessionId]);
};

export default useJoinGroup;
